import { Collapse, IconButton, Paper, Stack, Typography } from '@mui/material'
import UpdateSubmissionStatusExamsList from './UpdateSubmissionStatusExamsList'
import { KeyboardArrowDownRounded, KeyboardArrowUpRounded } from '@mui/icons-material'
import { useParams } from 'react-router-dom'
import { useGetTimePointSubmissionDetails } from '@features/subject/hooks/useGetTimePointSubmissionDetails'
import { useState } from 'react'

export default function ExpandableUpdateSubmissionStatusExam({
  selectedExams,
  isExpandable = true,
}: {
  selectedExams: Set<string>
  isExpandable?: boolean
}) {
  const [isOpen, setIsOpen] = useState(true)
  const toggleOpen = () => setIsOpen((prev) => !prev)

  const { studyId, subjectId, timepointSubmissionId } = useParams()
  const { data, error, isLoading } = useGetTimePointSubmissionDetails(
    ['TimePointsDetails', studyId!, subjectId!, timepointSubmissionId!],
    {
      study_id: studyId!,
      subject_id: subjectId!,
      timepoint_submission_id: timepointSubmissionId!,
    },
  )

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (error || !data || !data.data) {
    return <div>Error: {error?.message}</div>
  }

  // Get original container submissions and requirements
  const container_submissions = data.data.timepoint_submission.container_submissions
  const container_requirements = data.data.timepoint_submission.container_requirements

  // Create filtered arrays that only include containers with exam_metadata_ids in selectedExams
  const filteredContainerIndices = container_submissions.reduce((indices, container, index) => {
    // Check if any exam metadata ID from this container is in selectedExams
    const hasSelectedExam = container.exam_metadata.some(
      (exam) => exam.exam_metadata_id && selectedExams.has(exam.exam_metadata_id),
    )
    if (hasSelectedExam) {
      indices.push(index)
    }
    return indices
  }, [] as number[])

  const filteredContainerSubmissions = filteredContainerIndices.map((index) => container_submissions[index])
  const filteredContainerRequirements = filteredContainerIndices.map((index) => container_requirements[index])

  return (
    <Paper
      sx={{
        borderRadius: 3,
        boxShadow: 1,
      }}
    >
      <Stack padding={4} spacing={4}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography color="text.primary" variant="h6">
            Exams
          </Typography>
          {isExpandable && (
            <IconButton onClick={toggleOpen}>
              {isOpen ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
            </IconButton>
          )}
        </Stack>
        <Collapse in={isExpandable ? isOpen : true} timeout="auto" unmountOnExit>
          <Stack spacing={4}>
            {filteredContainerSubmissions.map((container, index) => (
              <Stack key={index} spacing={3}>
                <Typography color="text.primary" variant="subtitle1">
                  {filteredContainerRequirements[index]?.name}
                </Typography>
                <UpdateSubmissionStatusExamsList
                  container_submission={container}
                  container_requirement={filteredContainerRequirements[index]}
                />
              </Stack>
            ))}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  )
}
